import { useState } from "react";
import {
  Anchor,
  Badge,
  Button,
  Checkbox,
  Group,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { IconDownload, IconRefresh } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { SafeDateInput } from "@/components/SafeDateInput";
import type {
  CarriersListResponse,
  ClientsListResponse,
  CompaniesListResponse,
  ReportResult,
  ReportRow,
} from "@/types/api";

// ReportPage — единая страница дебиторки и кредиторки.
// Параметр kind определяет заголовок, endpoint и подписи колонок.
// Соответствует TZ 01.07.2026 п.5.а/б — «как в старой программе».

export type ReportKind = "receivables" | "payables";

interface Props {
  kind: ReportKind;
}

function fmtMoney(n: number, currency = "RUR"): string {
  return `${n.toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
}

function fmtDate(iso?: string): string {
  if (!iso) return "";
  const d = iso.slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(d);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : d;
}

function toDateISO(d: Date | null | undefined): string {
  if (!d) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export function ReportPage({ kind }: Props) {
  const label = kind === "receivables" ? "Дебиторская задолженность" : "Кредиторская задолженность";
  const contragentLabel = kind === "receivables" ? "Заказчик" : "Перевозчик";
  const endpoint = kind === "receivables" ? "/api/v1/reports/receivables" : "/api/v1/reports/payables";

  // Дефолт: last 6 months → сегодня. Заказчик обычно смотрит недавние заявки.
  const [dateFrom, setDateFrom] = useState<Date | null>(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 6);
    return d;
  });
  const [dateTo, setDateTo] = useState<Date | null>(new Date());
  const [asOf, setAsOf] = useState<Date | null>(new Date());
  const [onlyDebt, setOnlyDebt] = useState(true);
  const [onlyNoOriginals, setOnlyNoOriginals] = useState(false);
  const [companyID, setCompanyID] = useState<string | null>(null);
  const [contragentID, setContragentID] = useState<string | null>(null);

  // Справочники для фильтров: свои фирмы + контрагент (заказчики или
  // перевозчики — зависит от отчёта). Как заказчик показывал в TM:
  // «по заказчику, по своей компании, на дату, по перевозчику».
  const { data: companiesData } = useQuery({
    queryKey: ["companies", "report-filter"],
    queryFn: async () =>
      (await api.get<CompaniesListResponse>("/api/v1/companies?page=1&page_size=200")).data,
    staleTime: 5 * 60 * 1000,
  });
  const { data: contragentsData } = useQuery({
    queryKey: [kind === "receivables" ? "clients" : "carriers", "report-filter"],
    queryFn: async () => {
      const path = kind === "receivables" ? "clients" : "carriers";
      const r = await api.get<ClientsListResponse | CarriersListResponse>(
        `/api/v1/${path}?page=1&page_size=200`,
      );
      return r.data;
    },
    staleTime: 5 * 60 * 1000,
  });

  const params = new URLSearchParams();
  if (dateFrom) params.set("date_from", toDateISO(dateFrom));
  if (dateTo) params.set("date_to", toDateISO(dateTo));
  if (asOf) params.set("as_of", toDateISO(asOf));
  if (onlyDebt) params.set("only_debt", "true");
  if (onlyNoOriginals) params.set("only_no_originals", "true");
  if (companyID) params.set("company_id", companyID);
  if (contragentID) params.set("contragent_id", contragentID);

  const q = useQuery({
    queryKey: [endpoint, params.toString()],
    queryFn: async () => (await api.get<ReportResult>(`${endpoint}?${params.toString()}`)).data,
  });

  const rows = q.data?.items ?? [];

  function exportCSV() {
    // Простой CSV-экспорт для excel (заказчик сам открывает).
    const header = [
      "№", "Дата", "Своя фирма", contragentLabel, "ИНН", "Маршрут",
      "Сумма", "Оплачено", "Долг",
      "Дата разгрузки", "Отправлено док-в", "Оригиналы получены",
      "Срок оплаты", "Просрочка (дней)", "Ответственный",
    ];
    const lines = rows.map((r) => [
      r.number_internal,
      fmtDate(r.document_date),
      r.company_name ?? "",
      r.contragent_name ?? "",
      r.contragent_inn ?? "",
      r.route ?? "",
      r.amount.toFixed(2).replace(".", ","),
      r.paid.toFixed(2).replace(".", ","),
      r.debt.toFixed(2).replace(".", ","),
      fmtDate(r.unloading_at),
      fmtDate(kind === "receivables" ? r.docs_sent_at : r.docs_received_at),
      fmtDate(r.originals_received_at),
      fmtDate(r.payment_due_date),
      r.overdue_days || "",
      r.responsible_name ?? "",
    ].map((c) => `"${String(c).replace(/"/g, '""')}"`).join(";"));
    // UTF-8 BOM для Excel; ; как разделитель (русская локаль).
    const csv = "﻿" + header.map((h) => `"${h}"`).join(";") + "\n" + lines.join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${kind}_${toDateISO(new Date())}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={3}>{label}</Title>
        <Group gap="xs">
          <Button
            size="xs"
            variant="light"
            leftSection={<IconRefresh size={14} />}
            onClick={() => q.refetch()}
            loading={q.isFetching}
          >
            Обновить
          </Button>
          <Button
            size="xs"
            variant="light"
            leftSection={<IconDownload size={14} />}
            onClick={exportCSV}
            disabled={rows.length === 0}
          >
            Excel/CSV
          </Button>
        </Group>
      </Group>

      <Paper p="md" withBorder>
        <Group gap="md" wrap="wrap">
          <SafeDateInput label="Период с" value={dateFrom} onChange={setDateFrom} />
          <SafeDateInput label="Период по" value={dateTo} onChange={setDateTo} />
          <SafeDateInput label="На дату (просрочка от)" value={asOf} onChange={setAsOf} />
          <Select
            label="Своя фирма"
            placeholder="Все"
            data={(companiesData?.items ?? []).map((c) => ({ value: c.id, label: c.name }))}
            value={companyID}
            onChange={setCompanyID}
            clearable
            searchable
            style={{ width: 200 }}
          />
          <Select
            label={contragentLabel}
            placeholder="Все"
            data={(contragentsData?.items ?? []).map((c) => ({ value: c.id, label: c.name }))}
            value={contragentID}
            onChange={setContragentID}
            clearable
            searchable
            style={{ width: 230 }}
          />
          <div style={{ marginTop: 26 }}>
            <Checkbox
              label="Только с долгом"
              checked={onlyDebt}
              onChange={(e) => setOnlyDebt(e.currentTarget.checked)}
            />
          </div>
          <div style={{ marginTop: 26 }}>
            <Checkbox
              label="Только без оригиналов"
              checked={onlyNoOriginals}
              onChange={(e) => setOnlyNoOriginals(e.currentTarget.checked)}
            />
          </div>
        </Group>
      </Paper>

      {q.data && (
        <Paper p="sm" withBorder>
          <Group gap="xl">
            <Text size="sm">Заявок: <strong>{q.data.count}</strong></Text>
            <Text size="sm">К оплате: <strong>{fmtMoney(q.data.total_amount)}</strong></Text>
            <Text size="sm">Оплачено: <strong>{fmtMoney(q.data.total_paid)}</strong></Text>
            <Text size="sm" c={q.data.total_debt > 0 ? "red" : "green"}>
              Долг: <strong>{fmtMoney(q.data.total_debt)}</strong>
            </Text>
          </Group>
        </Paper>
      )}

      <Paper withBorder p={0} style={{ overflowX: "auto" }}>
        <Table striped highlightOnHover withColumnBorders>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>№</Table.Th>
              <Table.Th>Дата</Table.Th>
              <Table.Th>Своя фирма</Table.Th>
              <Table.Th>{contragentLabel}</Table.Th>
              <Table.Th>ИНН</Table.Th>
              <Table.Th>Маршрут</Table.Th>
              <Table.Th ta="right">Сумма</Table.Th>
              <Table.Th ta="right">Оплачено</Table.Th>
              <Table.Th ta="right">Долг</Table.Th>
              <Table.Th>Разгрузка</Table.Th>
              <Table.Th>{kind === "receivables" ? "Отправлено" : "Принято"}</Table.Th>
              <Table.Th>Оригиналы</Table.Th>
              <Table.Th>Срок оплаты</Table.Th>
              <Table.Th>Просрочка</Table.Th>
              <Table.Th>Ответственный</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {q.isLoading && (
              <Table.Tr>
                <Table.Td colSpan={15} ta="center">Загрузка...</Table.Td>
              </Table.Tr>
            )}
            {!q.isLoading && rows.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={15} ta="center">
                  <Text c="dimmed">Нет данных по фильтру</Text>
                </Table.Td>
              </Table.Tr>
            )}
            {rows.map((r) => (
              <ReportTableRow key={r.order_id} row={r} kind={kind} />
            ))}
          </Table.Tbody>
        </Table>
      </Paper>
    </Stack>
  );
}

function ReportTableRow({ row, kind }: { row: ReportRow; kind: ReportKind }) {
  const rowStyle = row.overdue_days > 0 ? { background: "rgba(255,0,0,0.04)" } : undefined;
  return (
    <Table.Tr style={rowStyle}>
      <Table.Td>
        <Anchor component={Link} to={`/orders/${row.order_id}`} size="sm">
          {row.number_internal}
        </Anchor>
      </Table.Td>
      <Table.Td>{fmtDate(row.document_date)}</Table.Td>
      <Table.Td>{row.company_name}</Table.Td>
      <Table.Td>{row.contragent_name}</Table.Td>
      <Table.Td ff="monospace">{row.contragent_inn}</Table.Td>
      <Table.Td>{row.route}</Table.Td>
      <Table.Td ta="right" ff="monospace">{row.amount.toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Table.Td>
      <Table.Td ta="right" ff="monospace">{row.paid.toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Table.Td>
      <Table.Td ta="right" ff="monospace" c={row.debt > 0 ? "red" : row.debt < 0 ? "orange" : "green"} fw={600}>
        {row.debt.toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </Table.Td>
      <Table.Td>{fmtDate(row.unloading_at)}</Table.Td>
      <Table.Td>{fmtDate(kind === "receivables" ? row.docs_sent_at : row.docs_received_at)}</Table.Td>
      <Table.Td>
        {row.originals_missing ? (
          <Badge color="red" variant="light" radius="sm" size="xs">нет</Badge>
        ) : (
          fmtDate(row.originals_received_at)
        )}
      </Table.Td>
      <Table.Td>{fmtDate(row.payment_due_date)}</Table.Td>
      <Table.Td>
        {row.overdue_days > 0 ? (
          <Badge color="red" variant="filled" radius="sm" size="sm">
            {row.overdue_days} дн.
          </Badge>
        ) : "—"}
      </Table.Td>
      <Table.Td>{row.responsible_name}</Table.Td>
    </Table.Tr>
  );
}
